import axios from "axios";
import qs from "qs";
import { URL } from "../../../../constants/Constants";

/**
 * Obtiene todos los datos de metales (oro y fornituras) desde el Excel del servidor
 * Incluye datos de Córdoba e India con sus respectivas tablas de stock
 * @param {number} currentScreenId - ID de la pantalla actual
 * @param {string} typeConnect - Tipo de conexión
 * @returns {Promise<Object|false>} Datos de metales o false si hay error
 */
export const getMetalsData = async (currentScreenId, typeConnect) => {
  const queryData = {
    query_type: currentScreenId,
    action: typeConnect,
    request_type: "metals_data"
  };

  try {
    const response = await axios.post(URL, qs.stringify(queryData), {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    let responseData = response.data;

    // Manejar respuesta que puede venir como string con JSON embebido
    if (typeof responseData === 'string' && responseData.includes('{')) {
      try {
        const jsonStart = responseData.indexOf('{');
        const jsonString = responseData.substring(jsonStart);
        responseData = JSON.parse(jsonString);
      } catch (parseError) {
        console.error("Error al parsear JSON de la respuesta:", parseError);
        return false;
      }
    }

    if (responseData.status === "ok" || responseData.status === "success") {
      return responseData.data;
    } else {
      console.log("Error al obtener datos de metales:", responseData.message);
      return false;
    }
  } catch (error) {
    console.error("Error al obtener datos de metales:", error);
    return false;
  }
};

/**
 * Obtiene datos específicos de metales por ubicación
 * @param {number} currentScreenId - ID de la pantalla actual
 * @param {string} typeConnect - Tipo de conexión
 * @param {string} location - Ubicación ('cordoba' o 'india')
 * @returns {Promise<Object|false>} Datos de metales de la ubicación o false si hay error
 */
export const getMetalsDataByLocation = async (currentScreenId, typeConnect, location) => {
  const queryData = {
    query_type: currentScreenId,
    action: typeConnect,
    request_type: "metals_data_by_location",
    location: location
  };

  try {
    const response = await axios.post(URL, qs.stringify(queryData), {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    let responseData = response.data;

    if (typeof responseData === 'string' && responseData.includes('{')) {
      try {
        const jsonStart = responseData.indexOf('{');
        const jsonString = responseData.substring(jsonStart);
        responseData = JSON.parse(jsonString);
      } catch (parseError) {
        console.error("Error al parsear JSON de la respuesta:", parseError);
        return false;
      }
    }

    if (responseData.status === "ok" || responseData.status === "success") {
      return responseData.data;
    } else {
      console.log(`Error al obtener datos de metales para ${location}:`, responseData.message);
      return false;
    }
  } catch (error) {
    console.error(`Error al obtener datos de metales para ${location}:`, error);
    return false;
  }
};

/**
 * Actualiza los datos de metales en el servidor
 * @param {number} currentScreenId - ID de la pantalla actual
 * @param {string} typeConnect - Tipo de conexión
 * @param {Object} metalsData - Datos de metales a actualizar
 * @returns {Promise<Object|false>} Resultado de la actualización o false si hay error
 */
export const updateMetalsData = async (currentScreenId, typeConnect, metalsData) => {
  const queryData = {
    query_type: currentScreenId,
    action: typeConnect,
    request_type: "update_metals_data",
    metals_data: JSON.stringify(metalsData)
  };

  try {
    const response = await axios.post(URL, qs.stringify(queryData), {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    let responseData = response.data;

    if (typeof responseData === 'string' && responseData.includes('{')) {
      try {
        const jsonStart = responseData.indexOf('{');
        const jsonString = responseData.substring(jsonStart);
        responseData = JSON.parse(jsonString);
      } catch (parseError) {
        console.error("Error al parsear JSON de la respuesta:", parseError);
        return false;
      }
    }

    if (responseData.status === "ok" || responseData.status === "success") {
      return responseData.data;
    } else {
      console.log("Error al actualizar datos de metales:", responseData.message);
      return false;
    }
  } catch (error) {
    console.error("Error al actualizar datos de metales:", error);
    return false;
  }
};

/**
 * Obtiene los datos de necesidad de metales desde el Excel del servidor
 * Incluye datos de necesidad para Córdoba e India
 * @param {number} currentScreenId - ID de la pantalla actual
 * @param {string} typeConnect - Tipo de conexión
 * @returns {Promise<Object|false>} Datos de necesidad de metales o false si hay error
 */
export const getMetalsNeedsData = async (currentScreenId, typeConnect) => {
  const queryData = {
    query_type: currentScreenId + 1, // Diferente ID para distinguir
    action: typeConnect,
    request_type: "metals_needs_data"
  };

  try {
    const response = await axios.post(URL, qs.stringify(queryData), {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    let responseData = response.data;

    // Manejar respuesta que puede venir como string con JSON embebido
    if (typeof responseData === 'string' && responseData.includes('{')) {
      try {
        const jsonStart = responseData.indexOf('{');
        const jsonString = responseData.substring(jsonStart);
        responseData = JSON.parse(jsonString);
      } catch (parseError) {
        console.error("Error al parsear JSON de la respuesta:", parseError);
        return false;
      }
    }

    if (responseData.status === "ok" || responseData.status === "success") {
      return responseData.data;
    } else {
      console.log("Error al obtener datos de necesidad de metales:", responseData.message);
      return false;
    }
  } catch (error) {
    console.error("Error al obtener datos de necesidad de metales:", error);
    return false;
  }
};

/**
 * Obtiene los datos de stock de Córdoba desde el Excel del servidor
 * Incluye datos específicos de stock para la ubicación de Córdoba
 * @param {number} currentScreenId - ID de la pantalla actual
 * @param {string} typeConnect - Tipo de conexión
 * @returns {Promise<Object|false>} Datos de stock de Córdoba o false si hay error
 */
export const getCordobaStockData = async (currentScreenId, typeConnect) => {
  const queryData = {
    query_type: currentScreenId + 2, // Diferente ID para distinguir
    action: typeConnect,
    request_type: "cordoba_stock_data"
  };

  try {
    const response = await axios.post(URL, qs.stringify(queryData), {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    let responseData = response.data;

    // Manejar respuesta que puede venir como string con JSON embebido
    if (typeof responseData === 'string' && responseData.includes('{')) {
      try {
        const jsonStart = responseData.indexOf('{');
        const jsonString = responseData.substring(jsonStart);
        responseData = JSON.parse(jsonString);
      } catch (parseError) {
        console.error("Error al parsear JSON de la respuesta:", parseError);
        return false;
      }
    }

    if (responseData.status === "ok" || responseData.status === "success") {
      return responseData.data;
    } else {
      console.log("Error al obtener datos de stock de Córdoba:", responseData.message);
      return false;
    }
  } catch (error) {
    console.error("Error al obtener datos de stock de Córdoba:", error);
    return false;
  }
};

/**
 * Obtiene los datos detallados del Excel para la tabla de filtros avanzados
 * Incluye datos de órdenes, artículos, referencias, metales, etc.
 * @param {number} currentScreenId - ID de la pantalla actual
 * @param {string} typeConnect - Tipo de conexión
 * @returns {Promise<Object|false>} Datos detallados del Excel o false si hay error
 */
export const getExcelDetailsData = async (currentScreenId, typeConnect) => {
  const queryData = {
    query_type: currentScreenId + 3, // Diferente ID para distinguir
    action: typeConnect,
    request_type: "excel_details_data"
  };

  try {
    const response = await axios.post(URL, qs.stringify(queryData), {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    let responseData = response.data;

    if (typeof responseData === 'string' && responseData.includes('{')) {
      try {
        const jsonStart = responseData.indexOf('{');
        const jsonString = responseData.substring(jsonStart);
        responseData = JSON.parse(jsonString);
      } catch (parseError) {
        console.error("Error al parsear JSON de la respuesta:", parseError);
        return false;
      }
    }

    if (responseData.status === "ok" || responseData.status === "success") {
      return responseData.data;
    } else {
      console.log("Error al obtener datos detallados del Excel:", responseData.message);
      return false;
    }
  } catch (error) {
    console.error("Error al obtener datos detallados del Excel:", error);
    return false;
  }
};